<template>
  <div class="search-results">
    <div class="row">
      <!-- Loaders -->
      <div v-if="loading" class="col-12 text-center">
        <div class="loader loader--bar">
          <span
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          <span class="loader__text">{{
            dict["indlæser produkter"] || "Indlæser..."
          }}</span>
        </div>
      </div>
      <div v-else class="search-results__header col-12">
        <template v-if="!termNotReadyForSearch">
          <p class="search-results__count">
            {{ `${nrOfResults} resultat${pad(nrOfResults, "er")} for` }}
          </p>
          <p class="search-results__term" v-if="searchTerm">
            “{{ searchTerm }}”
          </p>
          <template v-if="!noResults">
            <button
              class="btn mb-3"
              :class="[view === 'products' ? 'btn-primary' : 'btn-secondary']"
              @click="setView('products')"
            >
              Produkter og Kategorier ({{
                products.length + categories.length
              }})
            </button>
            <button
              class="btn mb-3"
              :class="[view === 'pages' ? 'btn-primary' : 'btn-secondary']"
              @click="setView('pages')"
            >
              Andet indhold ({{ pages.length }})
            </button>
          </template>
          <template v-if="noResultMessage">
            <p class="search-results__tips-label mt-5">
              Gode råd til din søgning
            </p>
            <p class="search-results__tips-text mb-0">
              Kontrollér stavningen, brug mere generelle termer, eller justér
              din søgeforespørgsel.
            </p>
          </template>
        </template>
        <template v-else>
          <p>Benyt søgefeltet i menuen for at starte en søgning</p>
        </template>
      </div>
      <template v-if="noResultMessage">
        <template v-if="popularCategories && popularCategories.length > 0">
          <hr class="search-results__hr" />
          <div class="col-12 px-4">
            <h2 class="mb-4">Populære kategorier</h2>
            <link-list :list="popularCategories" type="button"></link-list>
          </div>
        </template>
        <template v-if="popularProducts && popularProducts.length > 0">
          <hr class="search-results__hr" />
          <div class="col-12">
            <h2 class="mb-4 px-2">Populære produkter</h2>
            <div class="row products px-0">
              <div
                v-for="product in popularProducts"
                class="col-6 col-sm-6 col-md-3 px-0"
              >
                <Tile
                  :product="product"
                  :settings="{ currency: settings ? settings.currency : 'DKK' }"
                  :collection-mode="false"
                  :dict="dict"
                >
                </Tile>
              </div>
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div
          class="text-center"
          :class="
            view === 'products'
              ? 'col-12'
              : 'col-12 col-lg-10 col-xl-8 offset-lg-1 offset-xl-2'
          "
        >
          <p class="search-results__label">
            {{
              view === "products"
                ? `${products.length} produkt${pad(
                    products.length,
                    "er"
                  )} | priser Inkl. moms`
                : `${pages.length} indholdsside${pad(pages.length, "r")}`
            }}
          </p>
        </div>
        <template v-if="view === 'products'">
          <div class="search-results__sidebar col-12 col-lg-3 mb-4">
            <div class="navigation__label mt-0 mb-3">
              {{
                `${categories.length} Kategori${pad(categories.length, "er")}`
              }}
            </div>
            <link-list
              :list="categories"
              :search-term="searchTerm"
              v-if="categories && categories.length > 0"
            ></link-list>
            <p v-else>-</p>
          </div>
          <div class="col-12 col-lg-9 px-lg-0">
            <div
              class="row products mt-0 mb-4"
              v-if="view === 'products' && products && products.length > 0"
            >
              <div
                v-for="product in products"
                class="col-6 col-md-3 col-lg-6 col-xl-4 px-0"
              >
                <Tile
                  :product="product.productData"
                  :settings="{ currency: settings.currency }"
                  :collection-mode="false"
                  :dict="dict"
                >
                </Tile>
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="view === 'pages'">
          <div class="col-12 col-lg-10 col-xl-8 offset-lg-1 offset-xl-2">
            <div
              class="search-results__pages"
              v-if="view === 'pages' && pages && pages.length > 0"
            >
              <div class="search-results__page" v-for="page in pages">
                <h2 class="search-results__page-title">
                  <a class="search-results__page-link" :href="page.url"
                    >{{ page.name }}
                  </a>
                </h2>
                <p class="search-results__page-desc" v-html="page.description"></p>
                <a class="search-results__page-url" :href="page.url" aria-hidden="true">Læs mere</a>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import LinkList from "./components/LinkList.vue";
import Tile from "./components/Tile.vue";
import axios from "axios";

export default Vue.extend({
  name: "Search",
  components: {
    LinkList,
    Tile
  },
  props: [],
  data() {
    return {
      loading: true,
      searchTerm: new URLSearchParams(window.location.search).get("q") ?? "",
      categories: [],
      pages: [],
      products: [],
      instantSearchOnTerm: ["is", "si", "te"],
      settings: null,
      popularCategories: null,
      popularProducts: null,
      dict: [],
      view: "products"
    };
  },
  mounted() {
    if (!this.termNotReadyForSearch) {
      this.search();
    } else {
      this.loading = false;
    }

    axios
      .get(
        `/umbraco/api/searchapi/productsdictionary?contextid=${window.umbracoContext?.id}`
      )
      .then((response) => {
        this.dict = response.data.dictionary;
      })
      .catch((error) => {
        console.error(error);
      });

    axios
      .get(`/api/init-search?id=${window.umbracoContext?.id}`)
      .then((response) => {
        const data = response.data;
        this.popularCategories = data.popularCategories;
        this.popularProducts = data.popularProducts;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  methods: {
    search() {
      this.loading = true;

      axios
        .get(`/api/search?query=${this.searchTerm}`)
        .then((resp) => {
          const data = resp.data;
          const hits = data?.hits;
          if (!data || !hits) return;

          this.products = hits.products ?? [];
          this.settings = data.settings;
          this.categories = hits.categories ?? [];
          this.pages = hits.pages ?? [];
          this.loading = false;
          if (
            this.products.length === 0 &&
            this.categories.length === 0 &&
            this.pages.length > 0
          ) {
            this.view = "pages";
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error(error);
        });
    },
    setView(view) {
      this.view = view;
    },
    pad(arrLength, value) {
      return arrLength === 0 || arrLength > 1 ? value : "";
    }
  },
  computed: {
    noResults() {
      return (
        this.products?.length === 0 &&
        this.categories?.length === 0 &&
        this.pages?.length === 0
      );
    },
    noResultMessage() {
      return (
        (this.view === "products" &&
          this.products?.length === 0 &&
          this.categories?.length === 0) ||
        (this.view === "pages" && this.pages?.length === 0)
      );
    },
    nrOfResults() {
      return this.products.length + this.categories.length + this.pages.length;
    },
    termNotReadyForSearch() {
      return (
        this.searchTerm.length < 3 &&
        !this.instantSearchOnTerm.includes(this.searchTerm.toLowerCase())
      );
    }
  },
  watch: {}
});
</script>
